import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import type { UnifiedSelectionResizeHandle } from '@/lib/transforms/types/unified-selection-resize-handle'

type UnifiedSelectionResizeShapeSnapshot = {
  id: string
  x: number
  y: number
  width: number
  height: number
  rotation?: number
  /** Captured once at gesture start so photo-fill offset re-derivation never reads live,
   *  frame-mutating shape state — see `photoFillInitials` on `UnifiedSelectionResizeSession`. */
  photoFill?: ShapePhotoFill
}

type UnifiedSelectionResizeChrome = {
  pivotX: number
  pivotY: number
  width: number
  height: number
  rotationDeg: number
}

export type UnifiedSelectionResizeSession = {
  direction: UnifiedSelectionResizeHandle
  chromeStart: UnifiedSelectionResizeChrome
  localInitial: { x: number; y: number; width: number; height: number }
  shapeInitials: Record<
    string,
    { x: number; y: number; width: number; height: number; rotation: number }
  >
  boundsInitials: Record<string, ShapeBounds>
  /**
   * Each photo-filled shape's `photoFill` at gesture start (pointer-down), frozen for the whole
   * session. Photo offset/scale re-derivation (`computePhotoFillOffsetAfterResize`) must read
   * this instead of live `shapesRef.current`/`shapes` state on every frame — that state already
   * carries the *previous* frame's clamped offset by the time the next frame runs, making the
   * "before" side of the offset math history-dependent (the offset can get permanently clamped
   * mid-drag even though the same start/end size on a single-shape resize would have preserved
   * it) instead of a clean function of (gesture-start rect, final rect).
   */
  photoFillInitials: Record<string, ShapePhotoFill | undefined>
  aspectLockAxis?: 'width' | 'height'
}

function computeSelectionUnion(
  selectedShapes: UnifiedSelectionResizeShapeSnapshot[],
  boundsById: Record<string, ShapeBounds | undefined>
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (selectedShapes.length === 0) return null

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const shape of selectedShapes) {
    const b = boundsById[shape.id]
    const bx = shape.x + (b?.x ?? 0)
    const by = shape.y + (b?.y ?? 0)
    const bw = b?.width ?? shape.width
    const bh = b?.height ?? shape.height

    minX = Math.min(minX, bx)
    minY = Math.min(minY, by)
    maxX = Math.max(maxX, bx + bw)
    maxY = Math.max(maxY, by + bh)
  }

  return { minX, minY, maxX, maxY }
}

export function createUnifiedSelectionResizeSession(params: {
  direction: UnifiedSelectionResizeHandle
  selectedShapes: UnifiedSelectionResizeShapeSnapshot[]
  shapeBoundsById: Record<string, ShapeBounds | undefined>
  chromeAtStart?: UnifiedSelectionResizeChrome | null
}): UnifiedSelectionResizeSession | null {
  const { direction, selectedShapes, shapeBoundsById, chromeAtStart } = params
  const chromeStart =
    chromeAtStart ??
    (() => {
      const union = computeSelectionUnion(selectedShapes, shapeBoundsById)

      if (!union) return null
      const width = union.maxX - union.minX
      const height = union.maxY - union.minY

      return {
        pivotX: union.minX + width / 2,
        pivotY: union.minY + height / 2,
        width,
        height,
        rotationDeg: 0,
      }
    })()

  if (!chromeStart || chromeStart.width <= 0 || chromeStart.height <= 0) return null

  return {
    direction,
    chromeStart: { ...chromeStart },
    localInitial: {
      x: -chromeStart.width / 2,
      y: -chromeStart.height / 2,
      width: chromeStart.width,
      height: chromeStart.height,
    },
    shapeInitials: Object.fromEntries(
      selectedShapes.map((shape) => [
        shape.id,
        {
          x: shape.x,
          y: shape.y,
          width: shape.width,
          height: shape.height,
          rotation: shape.rotation ?? 0,
        },
      ])
    ),
    boundsInitials: Object.fromEntries(
      selectedShapes.map((shape) => [
        shape.id,
        shapeBoundsById[shape.id] ?? { x: 0, y: 0, width: shape.width, height: shape.height },
      ])
    ),
    photoFillInitials: Object.fromEntries(
      selectedShapes.map((shape) => [shape.id, shape.photoFill])
    ),
    aspectLockAxis: undefined,
  }
}
